import clsx from 'clsx';
import React, { useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Column } from 'react-table';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import Datatable from '../../../../../modules/components/datatable/Datatable';
import Header from '../../../../../modules/components/datatable/Header';
import UnlabeledBulletColumns from '../../../../../modules/components/list/UnlabeledBulletColumns';
import ListLoading from '../../../../../modules/components/loading/ListLoading';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { isoCountryMap } from '../../../../../modules/helpers/isoCountryMap';
import { DataSiftField, SiService, siteIntelligenceServiceLabel } from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import { AddWebContent } from '../form/AddWebContent';
import { ServiceSelector, UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { HistoryModal } from '../modal/HistoryModal';
import SummaryTable, { SummaryRow } from '../table/SummaryTable';
import { getFieldUpdate, SiRequestUser } from '../utils';

interface Sort {
  name: string;
  sort: 1 | -1;
}

interface Props {
  data: any;
  reviewer: SiRequestUser[];
  status: string;
  refetch: () => void;
  isEditable?: boolean;
  isSaving?: boolean;
  onUpdate?: UsePageDataManagerResult['updateField'];
  onSave?: UsePageDataManagerResult['saveService'];
  onReset?: UsePageDataManagerResult['resetService'];
  checkDirty?: UsePageDataManagerResult['isServiceDirty'];
}

export default function WebContentDetail({
  data,
  reviewer,
  status,
  refetch,
  isEditable,
  isSaving,
  onUpdate,
  onSave,
  onReset,
  checkDirty,
}: Props) {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id: `SI_WEB_CONTENT_DETAIL.${id}` });
  const { setToast } = useToast();
  const emDash = '—';
  const inputFieldClassName = 'form-control form-control-sm fw-normal text-dark';

  const [showDetail, setShowDetail] = useState(false);
  const defaultShowAddFields = {
    [DataSiftField.COMPANY_NAME]: false,
    [DataSiftField.PHONE]: false,
    [DataSiftField.EMAIL]: false,
    [DataSiftField.ADDRESS]: false,
  };
  const [showAddFields, setShowAddFields] =
    useState<Partial<Record<DataSiftField, boolean>>>(defaultShowAddFields);
  const [isEditing, setIsEditing] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const serviceDataSelector: ServiceSelector = (record) =>
    record?.service === SiService.DATA_SIFT_SERVICE && record?.queue_id === data?.queue_id;
  const serviceDataPaths = [
    'result.company_name',
    'result.phone',
    'result.email',
    'result.address',
  ];
  const isDirty = checkDirty && checkDirty(serviceDataSelector, serviceDataPaths);

  const params = useParams();
  const sessionPaging = JSON.parse(sessionStorage.getItem(params['*'])); // get paging from session
  const [paging, setPaging] = useState({
    offset: sessionPaging?.offset ?? 0,
    limit: sessionPaging?.limit ?? 15,
  });

  const [sort, setSort] = useReducer(
    (current: Sort, field: Sort['name']): Sort => {
      if (field === current.name) {
        return { name: field, sort: current.sort === 1 ? -1 : 1 };
      } else {
        return { name: field, sort: 1 };
      }
    },
    {
      name: 'created_at',
      sort: -1,
    }
  );
  const results = data;

  const isoCountryOptions = useMemo(
    () =>
      Object.entries(isoCountryMap).map(([code, label]) => ({
        value: code,
        label: label,
      })),
    [isoCountryMap]
  );

  const toggleAddField = (fieldType: DataSiftField) => {
    setShowAddFields((prev) => ({
      ...prev,
      [fieldType]: !prev[fieldType],
    }));
  };

  const handleSave = async () => {
    await onSave(serviceDataSelector, serviceDataPaths);
    setIsEditing(false);
  };

  const handleReset = () => {
    onReset(serviceDataSelector);
  };

  const renderAddFieldSection = (fieldType: DataSiftField) => {
    return (
      isEditable &&
      showAddFields[fieldType] && (
        <AddWebContent
          type={fieldType}
          show={showAddFields[fieldType]}
          onClose={() => toggleAddField(fieldType)}
          onUpdate={onUpdate}
          serviceSelector={serviceDataSelector}
        />
      )
    );
  };

  const reviewerNames: SiRequestUser[] = reviewer;
  const companyFieldUpdates = getFieldUpdate('result.company_name', data?.field_updates);
  const phoneFieldUpdates = getFieldUpdate('result.phone', data?.field_updates);
  const emailFieldUpdates = getFieldUpdate('result.email', data?.field_updates);
  const addressFieldUpdates = getFieldUpdate('result.address', data?.field_updates);
  const getReviewerEmail = (userId?: string) =>
    reviewerNames?.find((reviewer) => reviewer.user_id === userId)?.email ?? userId;
  const getReviewerNames = (fieldUpdates: { updated_by: string }[]) =>
    Array.from(
      new Set(
        fieldUpdates.map((fieldUpdate) => getReviewerEmail(fieldUpdate.updated_by)).filter(Boolean)
      )
    );

  const companyReviewerNames = getReviewerNames(companyFieldUpdates);
  const phoneReviewerNames = getReviewerNames(phoneFieldUpdates);
  const emailReviewerNames = getReviewerNames(emailFieldUpdates);
  const addressReviewerNames = getReviewerNames(addressFieldUpdates);
  const hasHistory = [
    companyFieldUpdates,
    phoneFieldUpdates,
    emailFieldUpdates,
    addressFieldUpdates,
  ].some((fieldUpdates) => fieldUpdates.length > 0);

  const renderReviewers = (reviewerList: string[]) =>
    (reviewerList.length > 0 && isEditable) ? (
      <div className='d-flex align-items-center gap-1 flex-wrap'>
        {reviewerList.map((reviewerName) => (
          <Avatar key={reviewerName} name={reviewerName} />
        ))}
      </div>
    ) : undefined;

  const dataSection: SummaryRow[] = [
    {
      key: 'CompanyName',
      field: t('COMPANY_NAME'),
      reviewer: renderReviewers(companyReviewerNames),
      render: () => {
        const values = results?.result?.company_name;
        return (
          <div className='vstack gap-4'>
            {values?.length > 0 ? (
              isEditing ? (
                <div className='vstack gap-1'>
                  {values.map((value, index) => (
                    <div className='d-flex gap-2' key={index}>
                      <i
                        className={clsx(
                          'cursor-pointer fs-1x align-middle text-muted fas fa-trash mt-3'
                        )}
                        title='Remove'
                        onClick={() =>
                          onUpdate(serviceDataSelector, (currentDraft) => {
                            if (currentDraft.result.company_name.length === 1) {
                              delete currentDraft.result.company_name;
                            } else {
                              currentDraft.result.company_name.splice(index, 1);
                            }
                          })
                        }
                      ></i>
                      <input
                        type='text'
                        className={inputFieldClassName}
                        key={index}
                        value={value}
                        onChange={(e) =>
                          onUpdate(
                            serviceDataSelector,
                            `result.company_name[${index}]`,
                            e.target.value
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <UnlabeledBulletColumns
                  data={values}
                  columnCount={values?.length > 2 ? 2 : 1}
                  shouldBreakText
                />
              )
            ) : (
              !isEditing && emDash
            )}

            {renderAddFieldSection(DataSiftField.COMPANY_NAME)}
          </div>
        );
      },
      action: isEditing && (
        <div className='d-flex' onClick={(e) => e.stopPropagation()}>
          <button
            className='btn btn-flush'
            title='Add'
            onClick={() => toggleAddField(DataSiftField.COMPANY_NAME)}
            disabled={showAddFields[DataSiftField.COMPANY_NAME]}
          >
            <i className={clsx('fs-3 align-middle fas fa-plus-square text-primary')}></i>
          </button>
        </div>
      ),
    },
    {
      key: 'Phone',
      field: t('PHONE'),
      reviewer: renderReviewers(phoneReviewerNames),
      render: () => {
        const values = results?.result?.phone;
        return (
          <div className='vstack gap-4'>
            {values?.length > 0 ? (
              isEditing ? (
                <div className='vstack gap-1'>
                  {values.map((value, index) => (
                    <div className='d-flex gap-2' key={index}>
                      <i
                        className={clsx(
                          'cursor-pointer fs-1x align-middle text-muted fas fa-trash mt-3'
                        )}
                        title='Remove'
                        onClick={() =>
                          onUpdate(serviceDataSelector, (currentDraft) => {
                            if (currentDraft.result.phone.length === 1) {
                              delete currentDraft.result.phone;
                            } else {
                              currentDraft.result.phone.splice(index, 1);
                            }
                          })
                        }
                      ></i>
                      <input
                        type='text'
                        className={inputFieldClassName}
                        key={index}
                        value={value}
                        onChange={(e) =>
                          onUpdate(serviceDataSelector, `result.phone[${index}]`, e.target.value)
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <UnlabeledBulletColumns
                  data={values}
                  columnWidth={'120px'}
                  columnCount={values.length > 4 ? 3 : 2}
                  shouldBreakText
                />
              )
            ) : (
              !isEditing && emDash
            )}

            {renderAddFieldSection(DataSiftField.PHONE)}
          </div>
        );
      },
      action: isEditing && (
        <div className='d-flex' onClick={(e) => e.stopPropagation()}>
          <button
            className='btn btn-flush'
            title='Add'
            onClick={() => toggleAddField(DataSiftField.PHONE)}
            disabled={showAddFields[DataSiftField.PHONE]}
          >
            <i className={clsx('fs-3 align-middle fas fa-plus-square text-primary')}></i>
          </button>
        </div>
      ),
    },
    {
      key: 'Email',
      field: t('EMAIL'),
      reviewer: renderReviewers(emailReviewerNames),
      render: () => {
        const values = results?.result?.email;
        return (
          <div className='vstack gap-4'>
            {values?.length > 0 ? (
              isEditing ? (
                <div className='vstack gap-1'>
                  {values.map((value, index) => (
                    <div className='d-flex gap-2' key={index}>
                      <i
                        className={clsx(
                          'cursor-pointer fs-1x align-middle text-muted fas fa-trash mt-3'
                        )}
                        title='Remove'
                        onClick={() =>
                          onUpdate(serviceDataSelector, (currentDraft) => {
                            if (currentDraft.result.email.length === 1) {
                              delete currentDraft.result.email;
                            } else {
                              currentDraft.result.email.splice(index, 1);
                            }
                          })
                        }
                      ></i>
                      <input
                        type='text'
                        className={inputFieldClassName}
                        key={index}
                        value={value}
                        onChange={(e) =>
                          onUpdate(serviceDataSelector, `result.email[${index}]`, e.target.value)
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <UnlabeledBulletColumns
                  data={values}
                  columnCount={values?.length > 2 ? 2 : 1}
                  shouldBreakText
                />
              )
            ) : (
              !isEditing && emDash
            )}

            {renderAddFieldSection(DataSiftField.EMAIL)}
          </div>
        );
      },
      action: isEditing && (
        <div className='d-flex' onClick={(e) => e.stopPropagation()}>
          <button
            className='btn btn-flush'
            title='Add'
            onClick={() => toggleAddField(DataSiftField.EMAIL)}
            disabled={showAddFields[DataSiftField.EMAIL]}
          >
            <i className={clsx('fs-3 align-middle fas fa-plus-square text-primary')}></i>
          </button>
        </div>
      ),
    },
    {
      key: 'Address',
      field: t('ADDRESS'),
      reviewer: renderReviewers(addressReviewerNames),
      render: () => {
        const values: { text?: string; city?: string; country_code?: string }[] =
          results?.result?.address;
        return (
          <div className='vstack gap-4'>
            {values?.length > 0 ? (
              isEditing ? (
                <div className='vstack gap-1'>
                  {values.map((value, index) => (
                    <div className='d-flex gap-2' key={index}>
                      <i
                        className={clsx(
                          'cursor-pointer fs-1x align-middle text-muted fas fa-trash mt-3'
                        )}
                        title='Remove'
                        onClick={() =>
                          onUpdate(serviceDataSelector, (currentDraft) => {
                            if (currentDraft.result.address.length === 1) {
                              delete currentDraft.result.address;
                            } else {
                              currentDraft.result.address.splice(index, 1);
                            }
                          })
                        }
                      ></i>
                      <div className='vstack'>
                        <input
                          type='text'
                          className={inputFieldClassName}
                          value={value?.text}
                          placeholder={t('ADDRESS')}
                          onChange={(e) =>
                            onUpdate(
                              serviceDataSelector,
                              `result.address[${index}].text`,
                              e.target.value
                            )
                          }
                        />
                        <div className='input-group input-group-sm'>
                          <label className='input-group-text' htmlFor='cityInput'>
                            {t('CITY')}
                          </label>
                          <input
                            type='text'
                            className={inputFieldClassName}
                            value={value?.city}
                            onChange={(e) =>
                              onUpdate(
                                serviceDataSelector,
                                `result.address[${index}].city`,
                                e.target.value
                              )
                            }
                          />
                          <label className='input-group-text' htmlFor='countrySelect'>
                            {t('COUNTRY')}
                          </label>
                          <Select
                            id='countrySelect'
                            placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                            styles={{
                              control: (baseStyles, state) => ({
                                ...baseStyles,
                                borderRadius: 0,
                                boxShadow: 'none',
                              }),
                            }}
                            classNames={{
                              control: (state) =>
                                clsx(
                                  'h-100 border-gray-300 rounded-end',
                                  state.isFocused ? 'border-gray-400' : ''
                                ),
                            }}
                            className='w-50'
                            options={isoCountryOptions}
                            value={isoCountryOptions.find(
                              (option) => option.value === value?.country_code
                            )}
                            onChange={(e) => {
                              onUpdate(
                                serviceDataSelector,
                                `result.address[${index}].country_code`,
                                e?.value
                              );
                            }}
                            menuPortalTarget={document.body}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <UnlabeledBulletColumns
                  data={values.map((value, index) => (
                    <div key={index}>
                      {value?.text || value}
                      <span className='d-block text-muted'>
                        {value?.city}
                        {value?.city && value?.country_code && ', '}
                        {value?.country_code}
                      </span>
                    </div>
                  ))}
                  columnCount={1}
                  shouldBreakText
                />
              )
            ) : (
              !isEditing && emDash
            )}

            {renderAddFieldSection(DataSiftField.ADDRESS)}
          </div>
        );
      },
      action: isEditing && (
        <div className='d-flex' onClick={(e) => e.stopPropagation()}>
          <button
            className='btn btn-flush'
            title='Add'
            onClick={() => toggleAddField(DataSiftField.ADDRESS)}
            disabled={showAddFields[DataSiftField.ADDRESS]}
          >
            <i className={clsx('fs-3 align-middle fas fa-plus-square text-primary')}></i>
          </button>
        </div>
      ),
    },
    {
      key: 'Messenger',
      field: t('WHATSAPP'),
      render: () => {
        const values = results?.result?.messenger;
        return (
          <div className='vstack gap-4'>
            {values?.length > 0 ? (
              <UnlabeledBulletColumns
                data={values}
                columnWidth={'120px'}
                columnCount={values?.length > 4 ? 3 : 2}
                shouldBreakText
              />
            ) : (
              emDash
            )}
          </div>
        );
      },
    },
  ];

  const columns: Column<any>[] = [
    {
      Header: () => (
        <Header
          sort={sort.name === 'url' ? sort.sort : null}
          onClick={() => {
            setSort('url');
          }}
        >
          {t('URL')}
        </Header>
      ),
      accessor: (row) => (
        <a
          href={row?.url}
          target='_blank'
          rel='noopener noreferrer'
          className='text-break text-wrap'
        >
          {row?.url}
        </a>
      ),
      minWidth: 120,
      id: 'url',
    },
    {
      Header: () => (
        <Header
          sort={sort.name === 'result.code' ? sort.sort : null}
          onClick={() => {
            setSort('result.code');
          }}
        >
          {t('HTTP_CODE')}
        </Header>
      ),
      accessor: (row) => <div>{row?.result?.code}</div>,
      minWidth: 95,
      id: 'result.code',
    },
    {
      Header: () => (
        <Header
          sort={sort.name === 'result.status' ? sort.sort : null}
          onClick={() => {
            setSort('result.status');
          }}
        >
          {t('HTTP_STATUS')}
        </Header>
      ),
      accessor: (row) => <div>{row?.result?.status}</div>,
      minWidth: 100,
      id: 'result.status',
    },
    {
      Header: () => (
        <Header
          sort={sort.name === 'result.company_name' ? sort.sort : null}
          onClick={() => {
            setSort('result.company_name');
          }}
        >
          {t('COMPANY_NAME')}
        </Header>
      ),
      accessor: (row) =>
        row?.result?.company_name?.length > 0 ? (
          <UnlabeledBulletColumns
            data={row.result.company_name}
            columnCount={1}
            shouldBreakText
            className={{ itemContainer: 'mb-1' }}
          />
        ) : (
          emDash
        ),
      minWidth: 120,
      id: 'result.company_name',
    },
    {
      Header: () => (
        <Header
          sort={sort.name === 'result.phone' ? sort.sort : null}
          onClick={() => {
            setSort('result.phone');
          }}
        >
          {t('PHONE')}
        </Header>
      ),
      accessor: (row) =>
        row?.result?.phone?.length > 0 ? (
          <UnlabeledBulletColumns
            data={row.result.phone}
            columnCount={1}
            shouldBreakText
            className={{ itemContainer: 'mb-1' }}
          />
        ) : (
          emDash
        ),
      minWidth: 120,
      id: 'result.phone',
    },
    {
      Header: () => (
        <Header
          sort={sort.name === 'result.email' ? sort.sort : null}
          onClick={() => {
            setSort('result.email');
          }}
        >
          {t('EMAIL')}
        </Header>
      ),
      accessor: (row) =>
        row?.result?.email?.length > 0 ? (
          <UnlabeledBulletColumns
            data={row.result.email}
            columnCount={1}
            shouldBreakText
            className={{ itemContainer: 'mb-1' }}
          />
        ) : (
          emDash
        ),
      minWidth: 120,
      id: 'result.email',
    },
    {
      Header: () => (
        <Header
          sort={sort.name === 'result.address' ? sort.sort : null}
          onClick={() => {
            setSort('result.address');
          }}
        >
          {t('ADDRESS')}
        </Header>
      ),
      accessor: (row) =>
        row?.result?.address?.length > 0 ? (
          <UnlabeledBulletColumns
            data={row.result.address}
            columnCount={1}
            shouldBreakText
            className={{ itemContainer: 'mb-1' }}
          />
        ) : (
          emDash
        ),
      minWidth: 120,
      id: 'result.address',
    },
  ];

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
        haveHistory={hasHistory && isEditable}
        onHistory={hasHistory ? () => setShowHistoryModal(true) : undefined}
        headerToolbar={
          <>
            {isEditable &&
              (isEditing ? (
                <button
                  onClick={(e) => {
                    e.currentTarget.blur();
                    if (isDirty) {
                      handleReset();
                    }
                    setIsEditing(false);
                    setShowAddFields(defaultShowAddFields);
                  }}
                  className={clsx('btn btn-sm btn-secondary')}
                  title='Cancel Edit'
                >
                  Cancel
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.currentTarget.blur();
                    setIsEditing(true);
                  }}
                  className={clsx('btn btn-sm btn-primary')}
                  title='Edit'
                >
                  Edit
                </button>
              ))}
          </>
        }
        isEditable={isEditable && isEditing}
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={handleSave}
        onReset={handleReset}
      >
        <div className='p-9 vstack gap-9'>
          <SummaryTable
            rows={dataSection}
            data={results}
            refetch={refetch}
            hideHeader={!isEditing}
            className='m-0'
          />
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>

      <HistoryModal
        show={showHistoryModal}
        handleClose={() => setShowHistoryModal(false)}
        data={{ results: { users: reviewer ?? [] } }}
        service_paths={[
          {
            ref_id: data?._id,
            paths: serviceDataPaths,
          },
        ]}
      />
    </>
  );
}
